import { useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

export type ExperienceMode = 'plain' | 'professional';

/**
 * Start with the answer, not the machinery.
 *
 * Plain mode is Fahali's default product experience: the complete answer and
 * evidence in ordinary language. Professional changes vocabulary
 * and presentation; they do not unlock facts that Plain silently removes.
 */
export const DEFAULT_MODE: ExperienceMode = 'plain';

const STORAGE_KEY = '@fahali_experience_mode';

/**
 * A subscription tier never makes the first experience harder to understand.
 * An explicit stored choice still wins — see loadFromStorageOnce.
 */
export function getDefaultModeForTier(_tier?: string): ExperienceMode {
  return DEFAULT_MODE;
}

interface UsePersonaReturn {
  mode: ExperienceMode;
  setMode: (m: ExperienceMode) => void;
  isPlain: boolean;
  isProfessional: boolean;
  isTrader: boolean;
}

// Shared module-level store: every component that calls usePersona used to
// hold its OWN useState seeded from AsyncStorage — switching persona in the
// status bar changed one card and left the rest of the app in the old mode
// until remount. All subscribers now update together, instantly.
let _mode: ExperienceMode = DEFAULT_MODE;
let _storageLoaded = false;
const _listeners = new Set<(m: ExperienceMode) => void>();

function notifyAll(m: ExperienceMode) {
  _listeners.forEach((l) => l(m));
}

/** True once we know the reader picked a layout themselves. */
let _explicit = false;

function loadFromStorageOnce() {
  if (_storageLoaded) return;
  _storageLoaded = true;
  AsyncStorage.getItem(STORAGE_KEY)
    .then((stored) => {
      if (stored === 'plain' || stored === 'professional' || stored === 'trader') {
        // Trader was a third presentation of the same facts. Keep the user's
        // preference, but migrate it into the combined Pro experience.
        _mode = stored === 'trader' ? 'professional' : stored;
        _explicit = true;
        notifyAll(_mode);
        if (stored === 'trader') {
          AsyncStorage.setItem(STORAGE_KEY, 'professional').catch(() => undefined);
        }
      }
    })
    .catch((error) => {
      console.error('Failed to load experience mode from storage:', error);
    });
}

/**
 * Seed the starting layout from the reader's tier without overriding a choice.
 * Today all tiers start in Plain mode; keeping this hook centralizes any future
 * change. It deliberately does not persist an inferred preference.
 */
export function seedModeFromTier(tier?: string) {
  if (_explicit) return;
  // An UNKNOWN tier still must not seed anything. `useBilling` returns
  // `undefined` while billing is loading and for every signed-out reader, and
  // an unknown tier is not a tier — see the `?? "retail"` defect class.
  if (!tier) return;
  const next = getDefaultModeForTier(tier);
  if (next === _mode) return;
  _mode = next;
  notifyAll(_mode);
}

export function usePersona(): UsePersonaReturn {
  const [mode, setLocal] = useState<ExperienceMode>(_mode);

  useEffect(() => {
    loadFromStorageOnce();
    const listener = (m: ExperienceMode) => setLocal(m);
    _listeners.add(listener);
    // Catch up in case the store changed between render and effect.
    if (_mode !== mode) setLocal(_mode);
    return () => {
      _listeners.delete(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMode = useCallback((m: ExperienceMode) => {
    _mode = m;
    // A hand-picked layout outranks any tier default from here on.
    _explicit = true;
    notifyAll(m);
    AsyncStorage.setItem(STORAGE_KEY, m).catch((error) => {
      console.error('Failed to persist experience mode:', error);
    });
  }, []);

  return {
    mode,
    setMode,
    isPlain: mode === 'plain',
    isProfessional: mode === 'professional',
    // Backwards-compatible capability flag for older Pro-only detail panels.
    // Pro now includes the former Trader evidence instead of hiding it.
    isTrader: mode === 'professional',
  };
}

/**
 * Migrates a legacy user to the persona system.
 *
 * Call this once during app startup (e.g., inside a root provider or splash-screen init).
 * If no mode has been persisted yet, start the user in Plain mode. Existing
 * portfolio data does not imply that someone wants technical language.
 *
 * @param _hasPortfolioData - Retained for API compatibility with startup callers.
 * @returns `true` if a migration was performed, `false` if the key already existed or an error occurred.
 */
export async function migrateLegacyUser(_hasPortfolioData: boolean): Promise<boolean> {
  try {
    const existing = await AsyncStorage.getItem(STORAGE_KEY);
    if (existing !== null) {
      return false;
    }

    const targetMode: ExperienceMode = 'plain';
    await AsyncStorage.setItem(STORAGE_KEY, targetMode);
    return true;
  } catch (error) {
    console.error('Failed to migrate legacy user experience mode:', error);
    return false;
  }
}
